// LIFE ← NOW → SELF. One world, three perspectives of the same moment.
// Opens on NOW. Pull down (or tap LIFE) to rise above the Mountain into the
// Compass; push up (or tap SELF) to tilt the same terrain into the Mountain
// and see the faculties in play. The background is always the Mountain —
// aerial for LIFE, tilted for NOW/SELF — so the user never leaves the world.
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Image, PanResponder, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Menu, Feather } from 'lucide-react-native';
import type { GearId } from '../olmps/ascension';
import type { UseTodayData } from '../olmps/useTodayData';
import { readNavSync, writeNav } from '../lib/navPersist';
import { useCurrentStep, type NowStep } from './useCurrentStep';
import { useSelf } from './useSelf';
import { NowView } from './NowView';
import { LifeView, type LifeMode } from './LifeView';
import { SelfView } from './SelfView';
import { DeitySheet } from './DeitySheet';
import { MOUNTAIN_TILT, TEMPLE_HALL, SEAT_BY_ID } from './roster';
import { DISPLAY_FONT, makeStyles, useV10 } from './theme';
import { OrbitBackdrop, useOrbit } from './orbit';

export type Lens = 'life' | 'now' | 'self';
const LENSES: Lens[] = ['life', 'now', 'self'];
const LENS_LABEL: Record<Lens, string> = { life: 'LIFE', now: 'NOW', self: 'SELF' };

export interface V10HomeProps {
  userId: string | undefined;
  today: UseTodayData;
  onOpenTool: (toolId: string, gear?: GearId, opts?: { initialTaskId?: string }) => void;
  onOpenDrawer: () => void;
  onCapture: () => void;
  onOpenAstra: () => void;
}

export function V10Home({ userId, today, onOpenTool, onOpenDrawer, onCapture, onOpenAstra }: V10HomeProps) {
  const t = useV10();
  const styles = useStyles();
  const [lens, setLensState] = useState<Lens>(() => readNavSync<Lens>('v10lens') ?? 'now');
  const setLens = (l: Lens) => {
    setLensState(l);
    writeNav('v10lens', l);
  };
  const [lifeMode, setLifeModeState] = useState<LifeMode>(() => readNavSync<LifeMode>('v10life') ?? 'compass');
  const setLifeMode = (m: LifeMode) => {
    setLifeModeState(m);
    writeNav('v10life', m);
  };
  const [stepIdx, setStepIdx] = useState(0);
  const [deityId, setDeityId] = useState<string | null>(null);

  const { steps, pace, loading, projects, spine } = useCurrentStep(userId, today);
  const self = useSelf(userId);
  const step: NowStep | null = steps[stepIdx] ?? steps[0] ?? null;
  // The Mountain orbit is shared by both LIFE views (compass overlays it).
  const orbit = useOrbit(step?.sector ?? null);
  const { width: winW, height: winH } = useWindowDimensions();
  const frameSize = Math.min(winW, 520);
  const frameTop = Math.round(winH * 0.17);
  useEffect(() => {
    if (stepIdx >= steps.length && steps.length) setStepIdx(0);
  }, [steps.length, stepIdx]);

  // Three plates behind the lenses: aerial (LIFE compass), tilted Mountain
  // (NOW, LIFE mountain), temple hall (SELF). Crossfade between them.
  const plate: 'aerial' | 'tilt' | 'hall' = lens === 'self' ? 'hall' : lens === 'life' ? 'aerial' : 'tilt';
  const aerial = useRef(new Animated.Value(plate === 'aerial' ? 1 : 0)).current;
  const hall = useRef(new Animated.Value(plate === 'hall' ? 1 : 0)).current;
  const rise = useRef(new Animated.Value(lens === 'life' ? 1 : 0)).current;
  useEffect(() => {
    const cfg = { duration: 650, easing: Easing.inOut(Easing.cubic), useNativeDriver: true };
    Animated.parallel([
      Animated.timing(aerial, { toValue: plate === 'aerial' ? 1 : 0, ...cfg }),
      Animated.timing(hall, { toValue: plate === 'hall' ? 1 : 0, ...cfg }),
      Animated.timing(rise, { toValue: lens === 'life' ? 1 : 0, ...cfg }),
    ]).start();
  }, [plate, lens, aerial, hall, rise]);

  // Vertical pull between lenses on the header area only (the views own
  // their own gestures — the ASTRA compass drags, the Step card swipes).
  const lensRef = useRef(lens);
  lensRef.current = lens;
  const pan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) => Math.abs(g.dy) > 24 && Math.abs(g.dy) > Math.abs(g.dx) * 2,
      onPanResponderRelease: (_e, g) => {
        const i = LENSES.indexOf(lensRef.current);
        if (g.dy > 60 && i > 0) setLens(LENSES[i - 1]);
        if (g.dy < -60 && i < LENSES.length - 1) setLens(LENSES[i + 1]);
      },
    }),
  ).current;

  const openStepTool = (s: NowStep) => {
    if (s.designId) writeNav('tkne', { designId: s.designId });
    onOpenTool(s.tool, s.gear, s.toolOpts);
  };
  const openSeatTool = (seatId: string) => {
    const seat = SEAT_BY_ID[seatId];
    setDeityId(null);
    if (!seat) return;
    if (step && step.tool === seat.shellTool) return openStepTool(step);
    if (seat.shellTool === 'astrs') return onOpenAstra();
    onOpenTool(seat.shellTool, seat.gear);
  };

  const aerialOpacity = aerial;
  const hallOpacity = hall;
  const tiltOpacity = Animated.subtract(1, Animated.add(aerial, hall));
  const tiltScale = rise.interpolate({ inputRange: [0, 1], outputRange: [1.18, 1.0] });
  const tiltShift = rise.interpolate({ inputRange: [0, 1], outputRange: [-60, 0] });

  return (
    <View style={styles.root}>
      <Animated.View style={[StyleSheet.absoluteFill, { opacity: tiltOpacity, transform: [{ scale: tiltScale }, { translateY: tiltShift }] }]}>
        <Image source={MOUNTAIN_TILT} style={styles.bg} resizeMode="cover" />
      </Animated.View>
      <Animated.View style={[StyleSheet.absoluteFill, { opacity: aerialOpacity }]}>
        <OrbitBackdrop frame={orbit.frame} width={frameSize} height={winH} top={frameTop} />
      </Animated.View>
      <Animated.View style={[StyleSheet.absoluteFill, { opacity: hallOpacity }]}>
        <Image source={TEMPLE_HALL} style={styles.bg} resizeMode="cover" />
      </Animated.View>
      {lens !== 'life' ? <LinearGradient colors={lens === 'self' ? t.scrimSelf : t.scrim} style={StyleSheet.absoluteFill} /> : null}

      <SafeAreaView style={{ flex: 1 }} edges={['top', 'bottom']}>
        <View {...pan.panHandlers}>
          <View style={styles.top}>
            <Pressable onPress={onOpenDrawer} style={styles.iconBtn} hitSlop={8}>
              <Menu size={20} color={t.ivory} />
            </Pressable>
            <Text style={styles.brand}>OLMPS</Text>
            <Pressable onPress={onCapture} style={styles.iconBtn} hitSlop={8} accessibilityLabel="Capture">
              <Feather size={18} color={t.goldBright} />
            </Pressable>
          </View>
          <View style={styles.tabs}>
            {LENSES.map((l) => (
              <Pressable key={l} onPress={() => setLens(l)} style={styles.tab} hitSlop={6}>
                <Text style={[styles.tabText, lens === l && styles.tabTextOn]}>{LENS_LABEL[l]}</Text>
                <View style={[styles.tabBar, lens === l && styles.tabBarOn]} />
              </Pressable>
            ))}
          </View>
        </View>

        <View style={{ flex: 1 }}>
          {lens === 'now' && (
            <NowView
              steps={steps}
              pace={pace}
              index={Math.min(stepIdx, Math.max(0, steps.length - 1))}
              onIndex={setStepIdx}
              self={self}
              loading={loading}
              onTalk={(id) => setDeityId(id)}
              onOpenTool={openStepTool}
              onCapture={onCapture}
              onSetDirection={() => onOpenTool('oracle', 'orient')}
            />
          )}
          {lens === 'life' && (
            <LifeView
              userId={userId}
              current={step}
              projects={projects}
              spine={spine}
              orbit={orbit}
              frameTopInRoot={frameTop}
              frameSize={frameSize}
              mode={lifeMode}
              onMode={setLifeMode}
              onFocusStep={() => setLens('now')}
              onOpenTool={(tool) => onOpenTool(tool)}
            />
          )}
          {lens === 'self' && <SelfView self={self} step={step} onOpenDeity={(id) => setDeityId(id)} onFocusStep={() => setLens('now')} />}
        </View>

        <View style={styles.nav}>
          {LENSES.map((l) => (
            <Pressable key={l} onPress={() => setLens(l)} style={styles.navItem} hitSlop={6}>
              <View style={[styles.navGlyph, lens === l && styles.navGlyphOn]}>
                <Text style={[styles.navGlyphText, lens === l && { color: t.onGold }]}>{l === 'life' ? '◎' : l === 'now' ? '●' : '▲'}</Text>
              </View>
              <Text style={[styles.navText, lens === l && { color: t.goldBright }]}>{l === 'life' ? 'Life' : l === 'now' ? 'Now' : 'Self'}</Text>
            </Pressable>
          ))}
        </View>
      </SafeAreaView>

      <DeitySheet
        seatId={deityId}
        seatView={deityId ? self.all.find((v) => v.seat.id === SEAT_BY_ID[deityId]?.id) : undefined}
        step={step}
        onClose={() => setDeityId(null)}
        onOpenTool={openSeatTool}
      />
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { flex: 1, backgroundColor: t.night, overflow: 'hidden' },
    bg: { width: '100%', height: '100%' },
    top: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingTop: 6 },
    iconBtn: { width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: t.glass, borderWidth: 1, borderColor: t.navyLine },
    brand: { fontFamily: DISPLAY_FONT, color: t.goldBright, fontSize: 24, letterSpacing: 6 },
    tabs: { flexDirection: 'row', justifyContent: 'center', gap: 34, paddingTop: 10, paddingBottom: 6 },
    tab: { alignItems: 'center', gap: 6 },
    tabText: { color: t.ivoryFaint, fontSize: 12.5, fontWeight: '800', letterSpacing: 3 },
    tabTextOn: { color: t.goldBright },
    tabBar: { width: 26, height: 2, borderRadius: 1, backgroundColor: 'transparent' },
    tabBarOn: { backgroundColor: t.gold },
    nav: { flexDirection: 'row', justifyContent: 'space-around', paddingTop: 8, paddingBottom: 6, borderTopWidth: 1, borderTopColor: t.navyLine, backgroundColor: t.glassStrong },
    navItem: { alignItems: 'center', gap: 3, minWidth: 70, paddingVertical: 2 },
    navGlyph: { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: t.navyLine },
    navGlyphOn: { backgroundColor: t.gold, borderColor: t.goldBright },
    navGlyphText: { color: t.ivorySoft, fontSize: 14 },
    navText: { color: t.ivoryFaint, fontSize: 11, fontWeight: '700' },
  }),
);
